/**
 * Pure enum VALUES for the lodeX Pro Postgres schema — no drizzle, by design.
 *
 * These four tuples are the same declarations that used to live in `enums.ts`, moved
 * here verbatim so a BROWSER can import them. `enums.ts` imports `drizzle-orm/pg-core`
 * for its `pgEnum`/`pgRole` declarations, and any web bundle that reached a value
 * through it pulled the whole of drizzle-orm in behind it — which is how ~22 drizzle
 * references ended up in the web-dispatch browser bundle.
 *
 * NOTHING drizzle-shaped belongs in this file. `enums.ts` re-exports everything here, so
 * every existing importer (36 files under `db/schema`, plus the API) is unaffected and
 * no call site changed.
 *
 * The contracts are UNCHANGED and still locked: `JOB_STATUS_CODES`'s order matches the
 * seed's `sort_order` and the boot assertion reads its length, so adding or reordering a
 * code is still a re-seed plus a migration. Moving the declaration does not move the
 * decision.
 */

/**
 * 16 canonical job status codes — master spec §8 (DEFINITIVE 11-STATE MODEL).
 *
 * Composition (master spec §8, docs/CLAUDE.md lines 360-398):
 *   - 13 sequential chain states (PENDING → ... → INVOICED)
 *   - 1 orthogonal modifier (PAUSED — applies to any non-terminal state)
 *   - 2 terminal states (CANCELLED, REJECTED)
 *
 * Order of this tuple MATCHES the seed sort_order column (Plan 02-06).
 * Changing order = re-seeding. Adding a code = ALTER CHECK at every consumer
 * + new seed migration + boot assertion update (Plan 02-07).
 */
export const JOB_STATUS_CODES = [
  // 13 sequential chain states (master spec §8)
  'PENDING',
  'EN_ROUTE_TO_PICKUP',
  'ARRIVED_PICKUP',
  'ONSITE_WORK_START',
  'ONSITE_WORK_END',
  'EN_ROUTE_TO_DROP_OFF',
  'DISPOSAL_IN',
  'UNLOADING_START',
  'DISPOSAL_OUT',
  'COMPLETED',
  'IN_REVIEW',
  'TO_BE_INVOICED',
  'INVOICED',
  // 1 orthogonal modifier (master spec §8)
  'PAUSED',
  // 2 terminal states (master spec §8)
  'CANCELLED',
  'REJECTED',
] as const;
export type JobStatusCode = (typeof JOB_STATUS_CODES)[number];

/**
 * 4 tenant types — master spec §5.
 *
 * Stable enum: adding a 5th type would be a multi-phase architectural change
 * (new tenant_users role catalog, new permission matrix rows, new RLS scopes).
 * pgEnum is therefore appropriate — its ALTER cost is acceptable in exchange
 * for column-level type safety.
 */
export const TENANT_TYPES = ['HAULER', 'OPERATOR', 'DISPOSAL', 'PLATFORM'] as const;
export type TenantType = (typeof TENANT_TYPES)[number];

/**
 * 11 tenant-user roles — master spec §6 (10) + master spec §35 (LEAD_DRIVER).
 *
 * Consolidated per DEC-015: `tenant_users` supersedes the separate
 * `driver_tenant_memberships` table from §35. Same shape; broader role enum.
 *
 * Stored as `text` with a CHECK constraint at the column level (NOT pgEnum)
 * because roles will evolve (e.g. compliance officer in Phase 14+). pgEnum
 * ALTER is fragile (RESEARCH Open Question #1, drizzle issue #4295);
 * text+CHECK rewrites are routine.
 */
export const TENANT_USER_ROLES = [
  // 10 from master spec §6
  'PLATFORM_ADMIN',
  'TENANT_ADMIN',
  'DISPATCH_MANAGER',
  'DISPATCHER',
  'TRUCK_PUSHER',
  'BILLING_MANAGER',
  'BILLING_STAFF',
  'DRIVER',
  'OPERATOR_ADMIN',
  'FOREMAN',
  // 1 from master spec §35 (consolidated per DEC-015)
  'LEAD_DRIVER',
  // Phase 16 / Task 16.2 (DEC-157) — DISPOSAL tenant admin created by the
  // approve.ts flow when a disposal-portal registration is approved. Mirrors
  // OPERATOR_ADMIN shape but for the DISPOSAL tenant_type. Migration 0064
  // extends tenant_users.role CHECK to accept this value.
  'DISPOSAL_ADMIN',
  // DEC-313 — four combined office roles (migration 0124 extends the
  // tenant_users.role CHECK and seeds the matching default_roles rows).
  'DISPATCHER_BILLING_STAFF',
  'DISPATCHER_BILLING_MANAGER',
  'DISPATCH_MANAGER_BILLING_STAFF',
  'DISPATCH_MANAGER_BILLING_MANAGER',
  // Operator portal rework PR 1 (migration 0200) — operator field user: selects the company's
  // wells and tanks and sees only those (lx-coord/operator-portal-design.md §1.1).
  'PUMPER',
] as const;
export type TenantUserRole = (typeof TENANT_USER_ROLES)[number];

/**
 * 3 tenant-user statuses — master spec §35 (consolidated per DEC-015).
 *
 * ACTIVE = currently working in the tenant.
 * SUSPENDED = temporarily disabled (e.g. CDL expired, awaiting renewal).
 * OFFBOARDED = permanently removed (offboarded_at timestamp set).
 */
export const TENANT_USER_STATUSES = ['ACTIVE', 'SUSPENDED', 'OFFBOARDED'] as const;
export type TenantUserStatus = (typeof TENANT_USER_STATUSES)[number];
